import { Inbox } from '@crehana/compass.icons.outline/v2/inbox';
import {
  Paginator,
  PaginatorNext,
  PaginatorPages,
  PaginatorPrevious,
} from '@crehana/compass.ui/v3/paginator';
import { format } from '@crehana/date/format';
import { DATE_FORMATS } from '@crehana/date/presets';
import { parseISO } from 'date-fns';
import { useState } from 'react';
import type { TAddCommentResult } from '../../../hooks/use-add-request-comment';
import { useTranslation } from '../../../i18n';
import type { TRequestComment } from '../../../types';
import { CommentForm } from './comment-form';

// DEVIATION (plan R3, no AC names it): the prototype paginates the thread at
// 3 comments per page (`comments-thread.tsx:16` in crehana-design). Kept
// verbatim — it is the approved design artifact.
const COMMENTS_PER_PAGE = 3;

type TCommentsThreadProps = {
  comments: TRequestComment[];
  submitting: boolean;
  onSubmit: (body: string) => Promise<TAddCommentResult>;
};

export const CommentsThread = ({
  comments,
  submitting,
  onSubmit,
}: TCommentsThreadProps) => {
  const { t, languageCode } = useTranslation();
  const [page, setPage] = useState(1);

  const totalPages = Math.max(
    1,
    Math.ceil(comments.length / COMMENTS_PER_PAGE),
  );
  const pageItems = comments.slice(
    (page - 1) * COMMENTS_PER_PAGE,
    page * COMMENTS_PER_PAGE,
  );

  // The server appends new comments at the end, so a success past the first
  // page must land on the page that now holds it. `comments.length` is the
  // count BEFORE the append, so +1 gives the post-append count.
  const handleSubmit = async (body: string) => {
    const result = await onSubmit(body);
    if (result === 'success') {
      setPage(Math.ceil((comments.length + 1) / COMMENTS_PER_PAGE));
    }
    return result;
  };

  const renderThreadBody = () => {
    if (comments.length === 0) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-cds-gap-3 tw-cds-p-7 tw-text-center">
          <div className="tw-cds-bg-layer-01 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-cds-p-5">
            <Inbox size={32} className="tw-cds-text-subtle" />
          </div>
          <p className="tw-cds-font-subtitle-01 tw-cds-text-default">
            {t('REQUEST_DETAIL_COMMENTS_EMPTY')}
          </p>
          <p className="tw-cds-font-body-02 tw-cds-text-subtle">
            {t('REQUEST_DETAIL_COMMENTS_EMPTY_SUBTITLE')}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="tw-cds-bg-subtle-01 tw-border-b tw-border-solid tw-cds-border-divider tw-cds-p-4">
          <span className="tw-cds-font-subtitle-01 tw-cds-text-default">
            {t('REQUEST_DETAIL_COMMENTS_THREAD_TITLE')}
          </span>
        </div>
        <div>
          {pageItems.map((comment, index) => (
            <div
              key={comment.id}
              className={
                index < pageItems.length - 1
                  ? 'tw-border-b tw-border-solid tw-cds-border-divider tw-cds-p-4'
                  : 'tw-cds-p-4'
              }
            >
              <div className="tw-flex tw-items-center tw-justify-between">
                {comment.user ? (
                  <span className="tw-cds-font-subtitle-01 tw-cds-text-default">
                    {comment.user.full_name}
                  </span>
                ) : null}
                <span className="tw-cds-font-body-02 tw-cds-text-subtle">
                  {format({
                    date: parseISO(comment.created_at),
                    pattern: DATE_FORMATS.SHORT,
                    languageCode,
                  })}
                </span>
              </div>
              <p className="tw-cds-font-body-02 tw-cds-text-subtle tw-whitespace-pre-line">
                {comment.comment}
              </p>
            </div>
          ))}
        </div>
        {totalPages > 1 ? (
          <div className="tw-border-t tw-border-solid tw-cds-border-divider tw-cds-p-3">
            <Paginator
              totalPages={totalPages}
              page={page}
              onPageChange={setPage}
              size="sm"
            >
              <div className="tw-flex tw-items-center tw-justify-center tw-cds-gap-2">
                <PaginatorPrevious />
                <PaginatorPages />
                <PaginatorNext />
              </div>
            </Paginator>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-cds-gap-4">
      <CommentForm onSubmit={handleSubmit} submitting={submitting} />
      <div className="tw-border tw-border-solid tw-cds-border-default tw-cds-rounded-s tw-overflow-hidden">
        {renderThreadBody()}
      </div>
    </div>
  );
};
